// ── The ONLY block that differs between apps/web and the manager app's copy
//    (apps/manager-desktop/src/screens/LabelQueue.tsx) — keep the rest identical.
import type { Item, LabelMark, PendingLabel } from '@erp/shared';
import { ArrowRight, Check, ChevronDown, Plus, RefreshCw, Search, Tags } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card } from '../../components/shadcn/index.js';
import { Button, Combobox, type ComboboxOption, Input } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney } from '../../lib/money.js';
// ── end of the app-specific block ──

/**
 * "Needs a label": products whose shelf label is missing (new) or out of date
 * (price or barcode changed since it was printed), worked out by the server
 * against the live product — so a price changed anywhere shows up here.
 *
 * Each product lists exactly WHICH of its barcodes needs a label: a carton
 * repriced is the carton's label, not the single piece's. Adding a product to
 * the print list selects exactly those barcodes.
 */

export interface PendingPage {
  items: Item[];
  total: number;
  counts: { items: number; new: number; changed: number };
  sources: Record<string, PendingLabel[]>;
}

type Filter = 'all' | 'new' | 'changed';
const PAGE = 50;
const ADD_MAX = 4000;
const MARK_CHUNK = 5000;

type Unprint = { itemId: string; key: string };

/** Record labels printed / forgotten, in chunks the API accepts. */
export async function postLabelMarks(printed: LabelMark[], unprinted: Unprint[] = []): Promise<void> {
  for (let i = 0; i < Math.max(printed.length, unprinted.length); i += MARK_CHUNK) {
    await api().post('/items/labels/marks', {
      printed: printed.slice(i, i + MARK_CHUNK),
      unprinted: unprinted.slice(i, i + MARK_CHUNK),
    });
  }
}

/**
 * The marks that put a pending label back exactly as it was: a label that had
 * never been printed is forgotten again; one that was out of date gets its OLD
 * printed values back, so it reads "price changed" again rather than "new".
 */
export function undoOf(entries: Array<{ itemId: string; label: PendingLabel }>): { printed: LabelMark[]; unprinted: Unprint[] } {
  const printed: LabelMark[] = [];
  const unprinted: Unprint[] = [];
  for (const { itemId, label } of entries) {
    if (label.reason === 'new' || label.printedAt === null) unprinted.push({ itemId, key: label.key });
    else printed.push({ itemId, key: label.key, barcode: label.printedBarcode, price: label.printedPrice, currencyCode: label.printedCurrencyCode });
  }
  return { printed, unprinted };
}

/** Which barcode of the product a pending label is: its unit / variant name. */
function sourceName(item: Item, key: string): string {
  if (key.startsWith('unit:')) return item.units?.find((u) => `unit:${u.id}` === key)?.name ?? '';
  if (key.startsWith('variant:')) return item.variants?.find((v) => `variant:${v.id}` === key)?.name ?? '';
  return item.unitOfMeasure ?? '';
}

export function LabelQueue({
  onAdd,
  version,
  categories,
  locale,
  canMarkAll,
}: {
  /** Queue these products for printing with exactly their pending barcodes selected. Returns how many were new rows. */
  onAdd: (items: Item[], sources: Record<string, PendingLabel[]>) => number;
  /** Bumped by the page after it records a print, so the list re-reads. */
  version: number;
  categories: ComboboxOption[];
  /** Data locale for product names. */
  locale: string;
  canMarkAll: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const { t: tc } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [catId, setCatId] = useState<string | undefined>();
  const [page, setPage] = useState<PendingPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [rev, setRev] = useState(0);
  const [armed, setArmed] = useState(false);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { const id = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(id); }, [search]);

  const params = (offset: number, limit = PAGE): Record<string, string | number> => ({
    filter, offset, limit, locale,
    ...(q ? { q } : {}),
    ...(catId ? { categoryId: catId } : {}),
  });

  // A fresh first page whenever the question changes or a print was recorded.
  useEffect(() => {
    let on = true;
    setBusy(true);
    void api().get<PendingPage>('/items/labels/pending', { params: params(0) })
      .then((r) => { if (on) { setPage(r.data); setSelected(new Set()); } })
      .catch(() => { if (on) setPage(null); })
      .finally(() => { if (on) setBusy(false); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, q, catId, locale, version, rev]);

  const reload = (): void => setRev((n) => n + 1);

  const loadMore = async (): Promise<void> => {
    if (!page) return;
    setBusy(true);
    try {
      const r = await api().get<PendingPage>('/items/labels/pending', { params: params(page.items.length) });
      setPage({ ...r.data, items: [...page.items, ...r.data.items], sources: { ...page.sources, ...r.data.sources } });
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  const money = (p: string | null, ccy: string | null): string => (p && ccy ? formatMoney(p, { locale: i18n.language, currency: ccy }) : p ?? '—');

  /** Mark these products' pending labels printed — with Undo. */
  const markItems = async (ids: string[]): Promise<void> => {
    if (!page) return;
    const entries = ids.flatMap((itemId) => (page.sources[itemId] ?? []).map((label) => ({ itemId, label })));
    if (!entries.length) return;
    const printed: LabelMark[] = entries.map(({ itemId, label }) => ({
      itemId, key: label.key, barcode: label.barcode, price: label.price, currencyCode: label.currencyCode,
    }));
    const undo = undoOf(entries);
    try {
      await postLabelMarks(printed);
      reload();
      toast.success(t('labels.queue.marked', { defaultValue: '{{n}} label(s) marked as printed', n: printed.length }), {
        action: {
          label: t('labels.queue.undo', { defaultValue: 'Undo' }),
          onClick: () => { void postLabelMarks(undo.printed, undo.unprinted).then(reload).catch(() => toast.error(tc('errors:server'))); },
        },
      });
    } catch { toast.error(tc('errors:server')); }
  };

  const addItems = (ids: string[]): void => {
    if (!page) return;
    const items = page.items.filter((it) => ids.includes(it.id));
    const n = onAdd(items, page.sources);
    toast.success(t('labels.queue.added', { defaultValue: 'Added {{n}} product(s) to print', n }));
  };

  /** Every product on the list (not just the loaded page), into the print list. */
  const addEverything = async (): Promise<void> => {
    setBusy(true);
    try {
      const items: Item[] = [];
      let sources: Record<string, PendingLabel[]> = {};
      for (let offset = 0; offset < ADD_MAX; offset += 200) {
        const r = await api().get<PendingPage>('/items/labels/pending', { params: params(offset, 200) });
        items.push(...r.data.items);
        sources = { ...sources, ...r.data.sources };
        if (r.data.items.length < 200) break;
      }
      const n = onAdd(items, sources);
      toast.success(t('labels.queue.added', { defaultValue: 'Added {{n}} product(s) to print', n }));
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  /** Two clicks, not a confirm(): a dialog a desktop webview may refuse to show. */
  const markEverything = async (): Promise<void> => {
    if (!armed) {
      setArmed(true);
      if (armTimer.current) clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => setArmed(false), 5000);
      return;
    }
    setArmed(false);
    setBusy(true);
    try {
      const r = await api().post<{ printed: number }>('/items/labels/mark-all', {
        filter, ...(q ? { q } : {}), ...(catId ? { categoryId: catId } : {}),
      });
      reload();
      toast.success(t('labels.queue.marked', { defaultValue: '{{n}} label(s) marked as printed', n: r.data.printed }));
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const counts = page?.counts ?? { items: 0, new: 0, changed: 0 };
  const allOn = items.length > 0 && items.every((it) => selected.has(it.id));
  const toggle = (id: string): void => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selIds = [...selected];
  const FILTERS: Array<{ id: Filter; label: string; n: number }> = [
    { id: 'all', label: t('labels.queue.filterAll', { defaultValue: 'All' }), n: counts.items },
    { id: 'new', label: t('labels.queue.filterNew', { defaultValue: 'New' }), n: counts.new },
    { id: 'changed', label: t('labels.queue.filterChanged', { defaultValue: 'Price changed' }), n: counts.changed },
  ];

  return (
    <Card className="flex flex-col gap-3 p-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-start">
        <Tags className="h-4 w-4 text-fg-muted" aria-hidden />
        <h2 className="flex-1 text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.queue.title', { defaultValue: 'Needs a label' })}</h2>
        {counts.items > 0 && <span className="rounded-full border border-warning px-2 py-0.5 text-xs font-semibold tabular-nums text-warning">{counts.items}</span>}
        <ChevronDown className={`h-4 w-4 text-fg-muted transition ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {open && (
        <>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${filter === f.id ? 'border-brand bg-brand-subtle text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
              >
                {f.label} <span className="tabular-nums opacity-70">{f.n}</span>
              </button>
            ))}
            <button type="button" onClick={reload} aria-label={t('labels.queue.refresh', { defaultValue: 'Refresh' })} title={t('labels.queue.refresh', { defaultValue: 'Refresh' })} className="ms-auto grid h-7 w-7 place-items-center rounded-md text-fg-muted hover:text-fg">
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} aria-hidden />
            </button>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute inset-y-0 start-2.5 my-auto h-4 w-4 text-fg-muted" aria-hidden />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('labels.queue.search', { defaultValue: 'Search name, code or barcode' })} className="h-9 ps-8" />
          </div>
          <Combobox size="sm" options={categories} value={catId} onChange={setCatId} placeholder={t('list.allCategories', { defaultValue: 'Category' })} ariaLabel={t('list.allCategories', { defaultValue: 'Category' })} />

          {!busy && items.length === 0 ? (
            <p className="flex items-center gap-2 rounded-md border border-success px-3 py-2 text-xs font-medium text-success">
              <Check className="h-4 w-4" aria-hidden />
              {q || catId || filter !== 'all'
                ? t('labels.queue.noMatch', { defaultValue: 'Nothing here needs a label.' })
                : t('labels.queue.upToDate', { defaultValue: 'Every shelf label is up to date.' })}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs text-fg-muted">
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" className="h-4 w-4 accent-brand" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(items.map((it) => it.id)))} />
                  {t('labels.queue.selectAll', { defaultValue: 'Select all' })}
                </label>
                <span className="ms-auto tabular-nums">{t('labels.queue.shown', { defaultValue: '{{n}} of {{total}}', n: items.length, total })}</span>
              </div>
              <ul className="flex max-h-[24rem] flex-col gap-1.5 overflow-auto pe-1">
                {items.map((it) => {
                  const labels = page?.sources[it.id] ?? [];
                  return (
                    <li key={it.id} className={`rounded-md border px-2.5 py-2 ${selected.has(it.id) ? 'border-brand bg-brand-subtle' : 'border-border bg-bg-subtle'}`}>
                      <div className="flex items-start gap-2">
                        <input type="checkbox" className="mt-0.5 h-4 w-4 flex-none accent-brand" checked={selected.has(it.id)} onChange={() => toggle(it.id)} aria-label={it.name} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{it.name}</span>
                          <span className="block truncate font-mono text-xs text-fg-muted">{it.code}</span>
                        </span>
                        <button type="button" onClick={() => addItems([it.id])} title={t('labels.queue.add', { defaultValue: 'Add to print' })} aria-label={t('labels.queue.add', { defaultValue: 'Add to print' })} className="grid h-7 w-7 flex-none place-items-center rounded-md border border-border text-fg-muted hover:border-brand hover:text-brand">
                          <Plus className="h-4 w-4" aria-hidden />
                        </button>
                        <button type="button" onClick={() => void markItems([it.id])} title={t('labels.queue.markPrinted', { defaultValue: 'Mark as printed' })} aria-label={t('labels.queue.markPrinted', { defaultValue: 'Mark as printed' })} className="grid h-7 w-7 flex-none place-items-center rounded-md border border-border text-fg-muted hover:border-success hover:text-success">
                          <Check className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                      <ul className="mt-1.5 flex flex-col gap-1 ps-6">
                        {labels.map((l) => (
                          <li key={l.key} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                            <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${l.reason === 'new' ? 'bg-brand-subtle text-brand' : 'border border-warning text-warning'}`}>
                              {l.reason === 'new'
                                ? t('labels.queue.reasonNew', { defaultValue: 'New' })
                                : l.reason === 'price'
                                  ? t('labels.queue.reasonPrice', { defaultValue: 'Price changed' })
                                  : t('labels.queue.reasonBarcode', { defaultValue: 'Barcode changed' })}
                            </span>
                            {sourceName(it, l.key) && <span className="font-medium text-fg">{sourceName(it, l.key)}</span>}
                            <span className="font-mono text-fg-muted" dir="ltr">{l.barcode}</span>
                            {/* Old → new as separate, isolated pieces in the page's direction, so an
                                Arabic page reads it right to left with the arrow pointing the same way. */}
                            <span className="ms-auto flex items-center gap-1 tabular-nums">
                              {l.reason === 'price' && (
                                <>
                                  <bdi className="text-fg-muted line-through">{money(l.printedPrice, l.printedCurrencyCode)}</bdi>
                                  <ArrowRight className="h-3 w-3 text-fg-muted rtl:rotate-180" aria-hidden />
                                </>
                              )}
                              <bdi className="font-semibold text-fg">{money(l.price, l.currencyCode)}</bdi>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
              {items.length < total && (
                <Button size="sm" variant="ghost" loading={busy} onClick={() => void loadMore()}>
                  {t('labels.queue.loadMore', { defaultValue: 'Show more ({{n}} left)', n: total - items.length })}
                </Button>
              )}
              <div className="grid grid-cols-2 gap-1.5">
                <Button size="sm" variant="secondary" disabled={selIds.length === 0} onClick={() => addItems(selIds)}>
                  <Plus className="h-3.5 w-3.5" aria-hidden />{t('labels.queue.addSelected', { defaultValue: 'Add ({{n}})', n: selIds.length })}
                </Button>
                <Button size="sm" variant="secondary" disabled={selIds.length === 0} onClick={() => void markItems(selIds)}>
                  <Check className="h-3.5 w-3.5" aria-hidden />{t('labels.queue.markSelected', { defaultValue: 'Printed ({{n}})', n: selIds.length })}
                </Button>
                <Button size="sm" variant="secondary" loading={busy} onClick={() => void addEverything()}>
                  {t('labels.queue.addAll', { defaultValue: 'Add all {{n}} to print', n: total })}
                </Button>
                {canMarkAll && (
                  <Button size="sm" variant={armed ? 'danger' : 'ghost'} loading={busy} onClick={() => void markEverything()}>
                    {armed
                      ? t('labels.queue.markAllConfirm', { defaultValue: 'Click again — mark {{n}} printed', n: total })
                      : t('labels.queue.markAll', { defaultValue: 'Mark all printed' })}
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}
